import { useCallback, useEffect, useRef, useState } from "react";
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { toast } from "react-toastify";
import api from "api/axios";
import Listtable from "components/common/ListTable";
import ComplaintModal from "./ComplaintModal";

const TABLE_HEADERS = ["Name", "Sub Complaints", "Speciality", "Action"];
const TABLE_KEYS = ["name", "subComplaintLabel", "specialityLabel", "action"];

const btnStyle = {
  width: "max-content",
  padding: "0.5rem 2rem",
  color: "#194AF5",
  bgcolor: "#E6EBFF",
  boxShadow: "none",
  borderRadius: "8px",
  ":hover": { bgcolor: "#E6EBFF", color: "#194AF5", boxShadow: "none" },
};

const saveBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const ComplaintMaster = ({ searchText = "" }) => {
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [activeComplaint, setActiveComplaint] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);
  const fileInputRef = useRef(null);

  const [debouncedSearch, setDebouncedSearch] = useState(searchText);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchText);
      setPageNumber(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchText]);

  const fetchComplaints = useCallback(() => {
    setLoading(true);

    api
      .get("/admin/complaints", {
        params: {
          page: pageNumber + 1,
          perPage: rowsPerPage,
          ...(debouncedSearch ? { search: debouncedSearch } : {}),
        },
      })
      .then((response) => {
        const payload = response?.data || {};
        const list = Array.isArray(payload.data) ? payload.data : [];

        setRows(
          list.map((item) => ({
            ...item,
            action: (
              <Box sx={{ display: "flex", gap: "0.25rem" }}>
                <IconButton
                  onClick={() => {
                    setActiveComplaint(item);
                    setModalOpen(true);
                  }}
                >
                  <EditIcon fontSize="small" color="primary" />
                </IconButton>
                <IconButton onClick={() => setDeleteTarget(item)}>
                  <DeleteOutlineIcon fontSize="small" color="error" />
                </IconButton>
              </Box>
            ),
          }))
        );

        const total = Number(payload?.meta?.total);
        setCount(Number.isFinite(total) ? total : list.length);
      })
      .catch((err) => {
        toast.error(
          err?.response?.data?.message || "Could not load complaints."
        );
      })
      .finally(() => setLoading(false));
  }, [pageNumber, rowsPerPage, debouncedSearch]);

  useEffect(() => {
    fetchComplaints();
  }, [fetchComplaints]);

  const handleModalClose = (reason = "save") => {
    setModalOpen(false);
    setActiveComplaint({});
    if (reason !== "close") fetchComplaints();
  };

  const handleDelete = async () => {
    if (!deleteTarget?._id) return;

    setBusy(true);
    try {
      await api.delete(`/admin/complaints/${deleteTarget._id}`);
      toast.success("Complaint deleted successfully");

      if (rows.length === 1 && pageNumber > 0) {
        setPageNumber(pageNumber - 1);
      } else {
        fetchComplaints();
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Could not delete this complaint."
      );
    } finally {
      setBusy(false);
      setDeleteTarget(null);
    }
  };

  const handleDownload = async (path, filename) => {
    setBusy(true);
    try {
      const response = await api.get(path, { responseType: "blob" });
      saveBlob(response.data, filename);
    } catch (err) {
      toast.error("Download failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const handleImport = async (event) => {
    const file = event?.target?.files?.[0];
    event.target.value = null;
    if (!file) return;

    setBusy(true);
    const body = new FormData();
    body.append("file", file);

    try {
      const response = await api.post("/admin/complaints/import", body, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const {
        created = 0,
        updated = 0,
        skipped = 0,
        failed = 0,
        specialityIgnored = 0,
      } = response?.data || {};

      const summary = `Import complete — ${created} created, ${updated} updated, ${skipped} skipped, ${failed} failed.`;

      if (failed > 0) {
        toast.warning(summary);
      } else {
        toast.success(summary);
      }

      if (specialityIgnored > 0) {
        toast.info(
          `${specialityIgnored} existing complaint(s) kept their current speciality mapping. Imported speciality was ignored — change it from the Edit screen.`,
          { autoClose: 8000 }
        );
      }

      setPageNumber(0);
      fetchComplaints();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Import failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: "0.5rem",
          padding: "1rem 0",
          flexWrap: "wrap",
        }}
      >
        <Button
          sx={btnStyle}
          variant="contained"
          disabled={busy}
          onClick={() =>
            handleDownload(
              "/admin/complaints/template",
              "complaint_master_template.xlsx"
            )
          }
        >
          Download Template
        </Button>

        <Button
          sx={btnStyle}
          variant="contained"
          disabled={busy}
          onClick={() => fileInputRef.current?.click()}
        >
          Import
          <input
            type="file"
            accept=".xlsx,.xls,.csv"
            ref={fileInputRef}
            hidden
            onChange={handleImport}
          />
        </Button>

        <Button
          sx={btnStyle}
          variant="contained"
          disabled={busy}
          onClick={() =>
            handleDownload(
              "/admin/complaints/export",
              "complaint_master_export.xlsx"
            )
          }
        >
          Export
        </Button>

        <Button
          sx={{ ...btnStyle, bgcolor: "#194AF5", color: "white", ":hover": {} }}
          variant="contained"
          disabled={busy}
          onClick={() => {
            setActiveComplaint({});
            setModalOpen(true);
          }}
        >
          Add Complaint
        </Button>
      </Box>

      <Listtable
        tableheader={TABLE_HEADERS}
        tablebody={rows}
        rowsPerPage={rowsPerPage}
        page={pageNumber}
        handleChangePage={(event, newPage) => setPageNumber(newPage)}
        handleChangeRowsPerPage={(event) => {
          setRowsPerPage(Number(event.target.value));
          setPageNumber(0);
        }}
        totalCount={count}
        dataKeyName={TABLE_KEYS}
        tag="complaints"
      />

      {modalOpen && (
        <ComplaintModal
          open={modalOpen}
          onClose={handleModalClose}
          data={activeComplaint}
        />
      )}

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete complaint?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{deleteTarget?.name}"? It will stop
            appearing in every hospital's Complaint Master.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" disabled={busy}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Backdrop open={loading || busy} sx={{ zIndex: 1200, color: "#fff" }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </>
  );
};

export default ComplaintMaster;
